"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import {
  HardHat,
  Menu,
  Smile,
  Timer,
  TrendingDown,
  WashingMachine,
  Wrench,
  X,
} from "lucide-react";
import Logo from "@/components/Logo";
import KpiMeter from "@/components/home/KpiMeter";
import SectionHead, { Eyebrow } from "@/components/home/SectionHead";
import MatchShowcase from "@/components/home/MatchShowcase";
import JobsIntakeShowcase from "@/components/home/JobsIntakeShowcase";
import MonteurDashboardShowcase from "@/components/home/MonteurDashboardShowcase";

const ROLE_HOME: Record<string, string> = {
  admin: "/beheer",
  monteur: "/monteur",
  opdrachtgever: "/opdrachtgever",
  ep_centraal: "/ep",
};
const homeFor = (r: string | null) => (r ? ROLE_HOME[r] : undefined) ?? "/monteur";

export default function Home() {
  const router = useRouter();
  const [role, setRole] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  // Altijd de echte rol valideren vóór redirect — een verouderde
  // localStorage-rol mag nooit een redirect-loop met de route-guards vormen.
  useEffect(() => {
    const token = localStorage.getItem("matchrite.access");
    if (!token) return;
    fetch(`${process.env.NEXT_PUBLIC_API_URL ?? ""}/api/auth/me/`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((me) => {
        if (!me) return;
        localStorage.setItem("matchrite.role", me.role);
        setRole(me.role);
        router.replace(homeFor(me.role));
      })
      .catch(() => {});
  }, [router]);

  return (
    <div className="flex min-h-screen flex-col bg-[var(--surface)] overflow-x-hidden">

      {/* ── Nav ─────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-30 border-b border-[var(--line)] bg-[color-mix(in_srgb,var(--surface)_88%,transparent)] backdrop-blur-md w-full">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-3 sm:px-6 sm:py-3.5">
          <Link href="/"><Logo size={18} /></Link>

          {/* Desktop rechts */}
          <div className="hidden sm:flex items-center gap-6">
            {role ? (
              <Link
                href={homeFor(role)}
                className="text-sm text-[var(--ink-3)] hover:text-[var(--ink)]"
              >
                Dashboard →
              </Link>
            ) : (
              <>
                <nav className="flex items-center gap-5">
                  <a href="#hoe-werkt-het" className="text-sm text-[var(--ink-3)] hover:text-[var(--ink)] transition-colors">
                    Hoe we werken
                  </a>
                  <Link href="/over-ons" className="text-sm text-[var(--ink-3)] hover:text-[var(--ink)] transition-colors">
                    Over ons
                  </Link>
                  <Link href="/login" className="text-sm text-[var(--ink-3)] hover:text-[var(--ink)] transition-colors">
                    Inloggen
                  </Link>
                </nav>
                <Link
                  href="/monteur-worden"
                  className="inline-flex items-center gap-2 rounded-full bg-[var(--brand)] px-4 py-2 text-sm font-medium text-[#0B0E0C] shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
                >
                  <Wrench size={13} strokeWidth={1.75} />
                  Aanmelden monteur
                </Link>
              </>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            className="sm:hidden flex items-center justify-center rounded-lg p-2 text-[var(--ink-2)] hover:bg-[var(--surface-raised)] transition-colors"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label="Menu"
          >
            {menuOpen ? <X size={20} strokeWidth={1.75} /> : <Menu size={20} strokeWidth={1.75} />}
          </button>
        </div>

        {/* Mobile dropdown */}
        {menuOpen && (
          <div className="sm:hidden border-t border-[var(--line)] bg-[var(--surface)] px-5 py-4 flex flex-col gap-1">
            <a
              href="#hoe-werkt-het"
              onClick={() => setMenuOpen(false)}
              className="rounded-lg px-3 py-2.5 text-sm font-medium text-[var(--ink-2)] hover:bg-[var(--surface-raised)] hover:text-[var(--ink)] transition-colors"
            >
              Hoe we werken
            </a>
            <Link
              href="/over-ons"
              onClick={() => setMenuOpen(false)}
              className="rounded-lg px-3 py-2.5 text-sm font-medium text-[var(--ink-2)] hover:bg-[var(--surface-raised)] hover:text-[var(--ink)] transition-colors"
            >
              Over ons
            </Link>
            <Link
              href="/login"
              onClick={() => setMenuOpen(false)}
              className="rounded-lg px-3 py-2.5 text-sm font-medium text-[var(--ink-3)] hover:bg-[var(--surface-raised)] hover:text-[var(--ink)] transition-colors"
            >
              Inloggen
            </Link>
            <Link
              href="/monteur-worden"
              onClick={() => setMenuOpen(false)}
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[var(--brand)] px-4 py-3 text-sm font-medium text-[#0B0E0C] shadow-sm"
            >
              <Wrench size={14} strokeWidth={1.75} />
              Aanmelden als monteur
            </Link>
          </div>
        )}
      </header>

      {/* ── Under Construction ──────────────────────────────────────── */}
      <div className="flex w-full items-center justify-center gap-2.5 border-b border-[var(--line)] bg-[var(--surface-sunk)] px-5 py-2.5 sm:gap-3 sm:px-6 sm:py-4">
        <HardHat size={16} strokeWidth={1.75} className="shrink-0 text-[var(--pool-algemeen)] sm:hidden" />
        <HardHat size={22} strokeWidth={1.75} className="hidden shrink-0 text-[var(--pool-algemeen)] sm:block" />
        <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-[var(--ink-2)] sm:text-[14px]">
          Under Construction
        </span>
        <HardHat size={22} strokeWidth={1.75} className="hidden shrink-0 text-[var(--pool-algemeen)] sm:block" />
      </div>

      {/* ── Hero + propositie (één geheel) ──────────────────────────── */}
      <section className="mx-auto flex w-full max-w-5xl flex-1 flex-col items-center px-5 pb-0 pt-10 text-center sm:px-6 sm:pt-28">
        <p className="mb-4 flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink-3)] sm:mb-5 sm:text-[11px]">
          <span className="inline-block h-px w-5 bg-[var(--brand)]" />
          De beste match voor witgoedreparaties
        </p>

        <h1 className="max-w-[680px] text-[clamp(30px,5.6vw,56px)] font-semibold leading-[1.06] tracking-[-0.028em] text-[var(--ink)] sm:leading-[1.04]">
          Voor elke job de juiste{" "}
          <span className="text-[var(--brand-strong)]">monteur</span>.<br />
          Voor elke monteur de juiste{" "}
          <span style={{ color: "#E3852A" }}>job</span>.
        </h1>

        <p className="mt-5 max-w-2xl text-[clamp(15px,1.8vw,17px)] leading-[1.65] text-[var(--ink-2)] sm:mt-6 sm:leading-[1.7]">
          {/* Mobiel: de kern in twee zinnen — de volle paragraaf wordt op 393px een muur. */}
          <span className="sm:hidden">
            MatchRite koppelt elke witgoedklus automatisch aan de monteur die écht
            past — op beschikbaarheid, regio, merkexpertise en tarief. Geen toeval in
            de planning, maar meer reparaties per dag.
          </span>
          <span className="hidden sm:inline">
            MatchRite koppelt elke witgoedklus automatisch aan de monteur die écht past: op
            basis van beschikbaarheid, regio, merkexpertise, kwaliteitsscore en tarief. Zo
            krijgen opdrachtgevers meer grip op servicecapaciteit, worden monteursuren slimmer
            benut en komt elke klant sneller bij de juiste vakman terecht. Geen toeval in de
            planning, maar slimme matchmaking voor betere service, efficiëntere routes en een
            sterker reparatienetwerk.
          </span>
        </p>

        {/* Resultaat-pills */}
        <div className="mt-7 grid w-full grid-cols-2 gap-2 sm:mt-8 sm:flex sm:w-auto sm:flex-wrap sm:justify-center sm:gap-2.5">
          {[
            { label: "Meer reparaties", Icon: Wrench },
            { label: "Minder faalkosten", Icon: TrendingDown },
            { label: "Kortere doorlooptijden", Icon: Timer },
            { label: "Hogere klanttevredenheid", Icon: Smile },
          ].map(({ label, Icon }) => (
            <span
              key={label}
              className="inline-flex items-center justify-center gap-1.5 rounded-full border border-[#14C07A]/25 bg-[#14C07A]/10 px-3 py-2 text-center text-[12px] font-medium leading-tight text-[#14C07A] sm:gap-2 sm:px-4 sm:text-[13px]"
            >
              <Icon size={14} strokeWidth={1.75} className="shrink-0 text-[#14C07A] sm:hidden" />
              <Icon size={15} strokeWidth={1.75} className="hidden shrink-0 text-[#14C07A] sm:block" />
              {label}
            </span>
          ))}
        </div>
      </section>

      {/* ── KPI-meters ──────────────────────────────────────────────── */}
      <section className="mx-auto w-full max-w-5xl px-5 pt-12 pb-0 sm:px-6 sm:pt-20">
        <div className="rounded-2xl bg-[var(--surface-raised)] px-5 py-5 shadow-sm sm:rounded-3xl sm:px-10 sm:py-10">
          <div className="flex flex-col divide-y divide-[var(--line)] sm:flex-row sm:items-center sm:gap-0 sm:divide-x sm:divide-y-0 sm:divide-[var(--line)]">
            <KpiMeter title="Matchingspercentage" from={0} to={95} max={100} suffix="%" durationMs={15000} tone="amber" />
            <KpiMeter title="Doorlooptijd" from={14} to={3.5} max={14} decimals={1} suffix="dagen" durationMs={15000} />
            <KpiMeter title="First Time Fix" from={0} to={92} max={100} suffix="%" durationMs={15000} tone="brand" />
          </div>
        </div>
      </section>

      {/* ── Producenten & retail ────────────────────────────────────── */}
      <section className="w-full py-12 overflow-hidden sm:py-20">
        <p className="mb-6 text-center font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink-3)] sm:mb-8 sm:text-[11px]">
          Gemaakt voor producenten en retail
        </p>
        <div className="mx-auto grid w-full max-w-5xl grid-cols-2 items-center justify-items-center gap-x-6 gap-y-8 px-5 sm:flex sm:flex-wrap sm:justify-center sm:gap-20 sm:px-6">
          {[
            { src: "/logos/fixid.svg",             alt: "FixID"             },
            { src: "/logos/samsung.svg",           alt: "Samsung"           },
            { src: "/logos/vestel.svg",            alt: "Vestel"            },
            { src: "/logos/sharp.svg",             alt: "Sharp"             },
          ].map((logo, i) => (
            <img
              key={i}
              src={logo.src}
              alt={logo.alt}
              className="h-[22px] sm:h-[34px] w-auto shrink-0 select-none"
            />
          ))}
        </div>
      </section>

      {/* ── Hoe we werken ───────────────────────────────────────────── */}
      <section id="hoe-werkt-het" className="mx-auto w-full max-w-5xl px-5 py-14 sm:px-6 sm:py-24">
        <p className="mb-3 text-center font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink-3)] sm:text-[11px]">
          Hoe we werken
        </p>
        <h2 className="mb-8 text-center text-[clamp(24px,4vw,36px)] font-semibold leading-tight tracking-[-0.025em] text-[var(--ink)] sm:mb-14">
          De slimste match voor elk reparatiejob
        </h2>

        <div className="grid gap-3 sm:grid-cols-3 sm:gap-4">
          {[
            {
              num: "01",
              label: "Opdrachtgever",
              title: "Job plaatsen",
              desc: "MatchRite is precies wat servicebedrijven nodig hebben om de juiste mensen op het juiste moment in te zetten. Stuur jobdetails naar het grootste netwerk van monteurs en rond werk af in één platform.",
              accent: "#14C07A",
            },
            {
              num: "02",
              label: "MatchRite",
              title: "Slim matchen",
              desc: "Snel de beste monteur vinden en selecteren op basis van gevraagde vaardigheden, locatie, merkkennis en projectspecificaties.",
              accent: "#4A5BD9",
            },
            {
              num: "03",
              label: "Monteur",
              title: "Job accepteren",
              desc: "Schaal capaciteit op of af op basis van de klantvraag. Zorg altijd voor kwaliteit, zonder extra overhead.",
              accent: "#E3852A",
            },
          ].map((s) => (
            <div
              key={s.num}
              className="relative overflow-hidden rounded-2xl bg-[var(--surface-raised)] p-5 shadow-[0_8px_30px_-18px_rgba(11,14,12,0.16)] sm:rounded-3xl sm:p-8"
            >
              <span
                className="absolute right-3 top-1 font-mono text-[56px] font-extrabold leading-none select-none sm:right-4 sm:top-2 sm:text-[72px]"
                style={{ color: s.accent, opacity: 0.08 }}
              >
                {s.num}
              </span>
              <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.08em] sm:mb-4" style={{ color: s.accent }}>
                {s.label}
              </p>
              <p className="mb-2 text-[17px] font-semibold leading-snug tracking-[-0.015em] text-[var(--ink)]">
                {s.title}
              </p>
              <p className="text-[13px] leading-[1.6] text-[var(--ink-2)]">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Integraties ─────────────────────────────────────────────── */}
      <section className="mx-auto w-full max-w-5xl px-5 py-14 sm:px-6 sm:py-20">
        <p className="mb-3 text-center font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink-3)] sm:text-[11px]">
          Integraties
        </p>
        <h2 className="mx-auto max-w-2xl text-center text-[24px] font-semibold leading-tight tracking-tight text-[var(--ink)] sm:text-3xl">
          Koppelt met je bestaande systemen
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-center text-[14px] leading-[1.6] text-[var(--ink-2)] sm:text-[15px]">
          MatchRite koppelt uitstekend met elk soort CRM en Field Service
          Management-systeem — zoals Insezo en FieldBuddy — en met corporate
          ERP zoals SAP. Jobs stromen binnen via de kanalen die je al gebruikt;
          wij regelen de matching en de capaciteit.
        </p>

        {/* CRM / FSM-logo's */}
        <div className="mt-8 grid grid-cols-2 gap-3 sm:mt-12 sm:grid-cols-3 sm:gap-x-6 sm:gap-y-8">
          {[
            { src: "/logos/afas.svg",        alt: "AFAS"            },
            { src: "/logos/salesforce.svg",  alt: "Salesforce"      },
            { src: "/logos/exact.svg",       alt: "Exact"           },
            { src: "/logos/dynamics365.svg", alt: "Dynamics 365"    },
            { src: "/logos/insezo.svg",      alt: "Insezo"          },
            { src: "/logos/fieldbuddy.svg",  alt: "FieldBuddy"      },
          ].map((logo) => (
            <div
              key={logo.alt}
              className="flex h-14 items-center justify-center rounded-xl border border-[var(--line)] bg-white px-3 shadow-[0_6px_22px_-16px_rgba(11,14,12,0.14)] transition-all hover:-translate-y-0.5 hover:border-[var(--ink-3)] hover:shadow-[0_10px_30px_-16px_rgba(11,14,12,0.18)] sm:h-16 sm:rounded-2xl sm:px-4"
            >
              <img
                src={logo.src}
                alt={logo.alt}
                className="h-6 w-auto select-none transition-all hover:scale-105 sm:h-8"
              />
            </div>
          ))}
        </div>

        {/* Corporate ERP-rij */}
        <div className="mt-6 flex flex-col items-center gap-3 rounded-2xl bg-[var(--surface-sunk)] px-5 py-5 sm:mt-8 sm:flex-row sm:justify-center sm:gap-6 sm:px-6 sm:py-6">
          <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink-3)] sm:text-[11px]">
            Ook voor corporate
          </span>
          <img
            src="/logos/sap.svg"
            alt="SAP"
            className="h-8 w-auto select-none sm:h-10"
          />
          <span className="max-w-xs text-center text-[13px] leading-[1.5] text-[var(--ink-2)] sm:text-left">
            Enterprise-koppeling met SAP voor producenten en grote retailketens.
          </span>
        </div>
      </section>

      {/* ── Jobs intake showcase ────────────────────────────────────── */}
      <JobsIntakeShowcase />

      {/* ── Monteur dashboard showcase ──────────────────────────────── */}
      <MonteurDashboardShowcase />

      {/* ── Match showcase ──────────────────────────────────────────── */}
      <MatchShowcase />

      {/* ── Dual CTA ────────────────────────────────────────────────── */}
      <section className="mx-auto w-full max-w-5xl px-5 pb-14 sm:px-6 sm:pb-24">
        <div className="grid gap-3 sm:grid-cols-2 sm:gap-4">

          {/* Monteur */}
          <div className="flex flex-col rounded-2xl bg-[var(--brand)] p-6 shadow-sm sm:rounded-3xl sm:p-10">
            <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.1em] text-[#0B0E0C]/50 sm:mb-4 sm:text-[11px]">
              Voor monteurs
            </p>
            <h3 className="mb-3 text-[22px] font-semibold leading-[1.15] tracking-[-0.02em] text-[#0B0E0C] sm:mb-4 sm:text-[24px]">
              Meer klussen,<br />minder zoeken
            </h3>
            <p className="mb-6 flex-1 text-[13px] leading-[1.6] text-[#0B0E0C]/65 sm:mb-8">
              Ontvang altijd de juiste klus die past bij jouw agenda, ervaring en werkgebied. Bepaal zelf hoeveel je verdient met MatchRite.
            </p>
            <div>
              <Link
                href="/monteur-worden"
                className="inline-flex items-center gap-2 rounded-full bg-[#0B0E0C] px-6 py-3 text-[14px] font-medium text-[var(--brand)] shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
              >
                <Wrench size={14} strokeWidth={1.75} />
                Aanmelden als monteur
              </Link>
            </div>
          </div>

          {/* Opdrachtgever */}
          <div className="flex flex-col rounded-2xl bg-[var(--ink)] p-6 shadow-sm sm:rounded-3xl sm:p-10">
            <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.1em] text-white/40 sm:mb-4 sm:text-[11px]">
              Voor opdrachtgevers
            </p>
            <h3 className="mb-3 text-[22px] font-semibold leading-[1.15] tracking-[-0.02em] text-white sm:mb-4 sm:text-[24px]">
              De juiste monteur,<br />elke keer
            </h3>
            <p className="mb-6 flex-1 text-[13px] leading-[1.6] text-white/55 sm:mb-8">
              MatchRite maakt de ideale en slimste match tussen elke job en de juiste eigen of externe monteur — met realtime inzicht in de status van elke job.
            </p>
            <div>
              <Link
                href="/signup?role=opdrachtgever"
                className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-[14px] font-medium text-[#0B0E0C] shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
                style={{ backgroundColor: "#E3852A" }}
              >
                <WashingMachine size={14} strokeWidth={1.75} />
                Job plaatsen
              </Link>
            </div>
          </div>

        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────────────── */}
      <footer className="border-t border-[var(--line)] bg-[var(--surface-sunk)] px-5 py-8 sm:px-6 sm:py-10">
        <div className="mx-auto max-w-5xl">

          {/* Top row */}
          <div className="flex flex-col gap-7 sm:flex-row sm:justify-between sm:gap-0">
            <div>
              <Logo size={16} />
              <p className="mt-3 text-[13px] leading-[1.7] text-[var(--ink-2)]">
                De slimste match voor elk reparatiejob.
              </p>
            </div>

            <div className="flex flex-col gap-1 text-[13px] text-[var(--ink-2)]">
              <p className="mb-1 font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--ink-3)]">Contact</p>
              <a href="mailto:info@matchrite.nl" className="hover:text-[var(--ink)] transition-colors">
                info@matchrite.nl
              </a>
              <p>Van Deventerlaan 31</p>
              <p>3528 AG Utrecht</p>
            </div>

            <div className="flex flex-col gap-1 text-[13px] text-[var(--ink-2)]">
              <p className="mb-1 font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--ink-3)]">Bedrijf</p>
              <p>KvK 63735458</p>
              <Link href="/login" className="hover:text-[var(--ink)] transition-colors">Inloggen</Link>
              <Link href="/over-ons" className="hover:text-[var(--ink)] transition-colors">Over ons</Link>
              <Link href="/monteur-worden" className="hover:text-[var(--ink)] transition-colors">Aanmelden als monteur</Link>
              <Link href="/aanmelden" className="hover:text-[var(--ink)] transition-colors">Aanmelden als opdrachtgever</Link>
            </div>
          </div>

          {/* Bottom row */}
          <div className="mt-8 flex items-center justify-between border-t border-[var(--line)] pt-6 sm:mt-10">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-[11px] text-[var(--ink-2)]">
            <span>© 2026 MatchRite</span>
            <Link href="/privacy" className="hover:text-[var(--ink)] transition-colors">Privacy & Cookies</Link>
          </div>
          </div>

        </div>
      </footer>

    </div>
  );
}
